import { IAccount, IAccountInfo, IAccountResponse } from "@interfaces";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LoadingStage } from "src/Interfaces/shared";
import { RootState } from ".";
import { accountApi } from "src/Api/accountApi";
import { lsAccount, lsEmail } from "@utilities";
import _ from "lodash";

export const getMetaData = createAsyncThunk<any>(
    "quotes/getMetaData",
    async (_, { rejectWithValue }) => {
        try {
            const response = await accountApi.getMetaData();
            return response.data.data;
        } catch (error) {
            console.error("Failed to get meta data:", error);
            return rejectWithValue("Failed to get meta data");
        }
    },
);

export const initializeAccountDetail = createAsyncThunk<IAccount, void>(
    "account/initializeAccountDetail",
    async (_, { rejectWithValue }) => {
        try {
            const res = await accountApi.userProfile();
            return res.data.data as IAccount;
        } catch (err: any) {
            const errorMessage = "Failed to initialize account detail";
            console.error(errorMessage, err);
            return rejectWithValue(errorMessage);
        }
    },
);

export const updateAccountDetail = createAsyncThunk<IAccountResponse, IAccountInfo>(
    "account/updateAccountDetail",
    async (payload: IAccountInfo, { rejectWithValue }) => {
        try {
            const res = await accountApi.updateUserDetail(payload);
            return res.data as IAccountResponse;
        } catch (err: any) {
            const errorMessage = "Failed to initialize account detail";
            console.error(errorMessage, err);
            return rejectWithValue(errorMessage);
        }
    },
);

export const loadAccountDetail = createAsyncThunk<IAccount, void>(
    "account/loadAccountDetail",
    async (_, { rejectWithValue }) => {
        try {
            const res = await accountApi.userProfile();
            return res.data.data as IAccount;
        } catch (err: any) {
            const errorMessage = "Failed to load account detail";
            console.error(errorMessage, err);
            return rejectWithValue(errorMessage);
        }
    },
);
interface MetaData {
    is_order_tab_enabled?: boolean;
    is_payment_link_sharing_enabled?: boolean;
    is_points_displayed?: boolean;
    is_commission_agent?: boolean;
    is_default_agent?: boolean;
    is_points_agent?: boolean;
    is_test_user?: boolean;
    is_upgrade_to_commission_enabled?: boolean;
}
interface IAccountState {
    account?: IAccount | null;
    metadata?: MetaData | null;
    loadingStage: LoadingStage;
    isLoading: boolean;
    error?: string | null;
}

const rawAccount = lsAccount.getItem();
const localAccount: IAccount | null =
    _.isNil(rawAccount) || _.isEmpty(rawAccount) ? null : (rawAccount as IAccount);

const initialState: IAccountState = {
    account: localAccount,
    metadata: null,
    loadingStage: _.isNil(localAccount)
        ? LoadingStage.NOT_INITIALIZED
        : LoadingStage.INITIALIZE_COMPLETE,
    isLoading: true,
};

const accountSlice = createSlice({
    name: "account",
    initialState: initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(getMetaData.pending, (state) => {
                state.error = null;
            })
            .addCase(getMetaData.rejected, (state, action: PayloadAction<any>) => {
                state.error = action.payload as string;
            })
            .addCase(getMetaData.fulfilled, (state, action: PayloadAction<any>) => {
                state.metadata = action.payload as MetaData;
            })
            .addCase(initializeAccountDetail.pending, (state) => {
                state.loadingStage = LoadingStage.IS_INITIALIZING;
                state.isLoading = true;
            })
            .addCase(initializeAccountDetail.rejected, (state, action: PayloadAction<any>) => {
                state.loadingStage = LoadingStage.INITIALIZE_FAILURE;
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(
                initializeAccountDetail.fulfilled,
                (state, action: PayloadAction<IAccount>) => {
                    state.loadingStage = LoadingStage.INITIALIZE_COMPLETE;
                    state.isLoading = false;
                    state.account = action.payload;
                    if (state.account) {
                        lsAccount.setItem(state.account);
                        if (state.account.email) {
                            lsEmail.setItem(state.account.email);
                        }
                    }
                },
            )
            .addCase(loadAccountDetail.pending, (state) => {
                state.loadingStage = LoadingStage.IS_LOADING;
                state.isLoading = true;
            })
            .addCase(loadAccountDetail.rejected, (state, action: PayloadAction<any>) => {
                state.loadingStage = LoadingStage.LOADING_FAILURE;
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(loadAccountDetail.fulfilled, (state, action: PayloadAction<IAccount>) => {
                state.loadingStage = LoadingStage.LOADING_COMPLETE;
                state.isLoading = false;
                state.account = action.payload;
                if (state.account) {
                    lsAccount.setItem(state.account);
                    if (state.account.email) {
                        lsEmail.setItem(state.account.email);
                    }
                }
            })
            .addCase(updateAccountDetail.pending, (state) => {
                state.loadingStage = LoadingStage.IS_LOADING;
                state.isLoading = true;
            })
            .addCase(updateAccountDetail.rejected, (state, action: PayloadAction<any>) => {
                state.loadingStage = LoadingStage.LOADING_FAILURE;
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(
                updateAccountDetail.fulfilled,
                (state, action: PayloadAction<IAccountResponse>) => {
                    state.loadingStage = LoadingStage.LOADING_COMPLETE;
                    state.isLoading = false;
                    state.account = action.payload.data;
                    if (state.account) {
                        lsAccount.setItem(state.account);
                        if (state.account.email) {
                            lsEmail.setItem(state.account.email);
                        }
                    }
                },
            );
    },
});

export const selectAccount = (state: RootState) => state.account;

export default accountSlice.reducer;
